import React from 'react';
import { Icon, Label, Message, Button } from 'semantic-ui-react';
import styled from 'styled-components';
import { useRetryProcessingMutation } from '../api/repositoryApi';
import { useToasts } from 'common/toast';

export enum ProcessingStatus {
  PENDING = 0,
  PROCESSING = 1,
  COMPLETED = 2,
  FAILED = 3,
}

export enum TranscriptionStatus {
  NOT_STARTED = 0,
  IN_PROGRESS = 1,
  COMPLETED = 2,
  FAILED = 3,
}

interface ProcessingStatusIndicatorProps {
  processingStatus: ProcessingStatus;
  transcriptionStatus?: TranscriptionStatus;
  processingError?: string;
  transcriptionError?: string;
  fileType: number; // FileTypes enum value
  fileId?: number; // Optional file ID for retry functionality
  retryCount?: number;
}

type StatusInfo = {
  icon?: 'clock outline' | 'spinner' | 'check circle' | 'exclamation triangle' | 'question circle';
  color: 'grey' | 'blue' | 'green' | 'red';
  text: string;
};

const StatusContainer = styled.div`
  margin: 1rem 0;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  gap: 0.5rem;
`;

const getProcessingStatusInfo = (status: ProcessingStatus): StatusInfo => {
  switch (status) {
    case ProcessingStatus.PENDING:
      return { icon: 'clock outline', color: 'grey', text: 'Pending processing' };
    case ProcessingStatus.PROCESSING:
      return { icon: 'spinner', color: 'blue', text: 'Processing content' };
    case ProcessingStatus.COMPLETED:
      return { icon: undefined, color: 'green', text: 'Processing complete' };
    case ProcessingStatus.FAILED:
      return { icon: 'exclamation triangle', color: 'red', text: 'Processing failed' };
    default:
      return { icon: 'question circle', color: 'grey', text: 'Unknown status' };
  }
};

const getTranscriptionStatusInfo = (status: TranscriptionStatus): StatusInfo => {
  switch (status) {
    case TranscriptionStatus.NOT_STARTED:
      return { icon: 'clock outline', color: 'grey', text: 'Transcription not started' };
    case TranscriptionStatus.IN_PROGRESS:
      return { icon: 'spinner', color: 'blue', text: 'Transcribing audio' };
    case TranscriptionStatus.COMPLETED:
      return { icon: undefined, color: 'green', text: 'Transcription complete' };
    case TranscriptionStatus.FAILED:
      return { icon: 'exclamation triangle', color: 'red', text: 'Transcription failed' };
    default:
      return { icon: 'question circle', color: 'grey', text: 'Unknown status' };
  }
};

const getFileTypeLabel = (fileType: number) => {
  switch (fileType) {
    case 0: // PDF
      return 'PDF';
    case 1: // MP4
      return 'MP4';
    case 2: // URL
      return 'URL';
    default:
      return 'File';
  }
};

export const ProcessingStatusIndicator: React.FC<ProcessingStatusIndicatorProps> = ({
  processingStatus,
  transcriptionStatus,
  processingError,
  transcriptionError,
  fileType,
  fileId,
  retryCount = 0,
}) => {
  const [retryProcessing, { isLoading: isRetrying }] = useRetryProcessingMutation();
  const { addToast } = useToasts();

  const handleRetry = async () => {
    if (!fileId) {
      addToast('File ID is required for retry', { appearance: 'error' });
      return;
    }

    try {
      await retryProcessing(fileId).unwrap();
      addToast('Processing restarted successfully', { appearance: 'success' });
    } catch (error) {
      addToast('Failed to restart processing', { appearance: 'error' });
    }
  };

  const MAX_RETRIES = 3;
  const isPermanentlyFailed =
    retryCount >= MAX_RETRIES &&
    (processingStatus === ProcessingStatus.FAILED ||
      transcriptionStatus === TranscriptionStatus.FAILED);
  const canRetry =
    fileId &&
    !isPermanentlyFailed &&
    (processingStatus === ProcessingStatus.FAILED ||
      transcriptionStatus === TranscriptionStatus.FAILED);
  const processingInfo = getProcessingStatusInfo(processingStatus);
  const transcriptionInfo =
    transcriptionStatus !== undefined ? getTranscriptionStatusInfo(transcriptionStatus) : null;
  const fileTypeLabel = getFileTypeLabel(fileType);

  const isMP4 = fileType === 1; // MP4 type
  const showTranscription = isMP4 && transcriptionStatus !== undefined;

  return (
    <StatusContainer>
      <h4>Processing Status</h4>
      {/* Inline all status badges in a single row */}
      <StatusRow>
        {/* Only show icon if not completed */}
        {processingInfo.icon && (
          <Icon
            name={processingInfo.icon}
            color={processingInfo.color}
            loading={processingStatus === ProcessingStatus.PROCESSING}
          />
        )}
        <Label color={processingInfo.color} basic>
          {processingInfo.text}
        </Label>
        {showTranscription && (
          <>
            {/* Only show icon if not completed */}
            {transcriptionInfo!.icon && (
              <Icon
                name={transcriptionInfo!.icon}
                color={transcriptionInfo!.color}
                loading={transcriptionStatus === TranscriptionStatus.IN_PROGRESS}
                style={{ marginLeft: 8 }}
              />
            )}
            <Label color={transcriptionInfo!.color} basic>
              {transcriptionInfo!.text}
            </Label>
          </>
        )}
      </StatusRow>

      {/* Error Messages */}
      {processingError && (
        <Message negative size="small">
          <Message.Header>Processing Error</Message.Header>
          <p>{processingError}</p>
        </Message>
      )}

      {transcriptionError && (
        <Message negative size="small">
          <Message.Header>Transcription Error</Message.Header>
          <p>{transcriptionError}</p>
        </Message>
      )}

      {/* Permanently Failed Notice */}
      {isPermanentlyFailed && (
        <Message warning size="small">
          <Message.Header>Processing Stopped</Message.Header>
          <p>This file has failed processing {retryCount} times and will no longer be retried automatically. You can still retry manually.</p>
        </Message>
      )}

      {/* Retry Button */}
      {(canRetry || isPermanentlyFailed) && (
        <StatusRow>
          <Button
            primary
            size="small"
            loading={isRetrying}
            onClick={handleRetry}
            icon="refresh"
            content="Retry Processing"
          />
        </StatusRow>
      )}

      {/* Processing Instructions */}
      {processingStatus === ProcessingStatus.PENDING && (
        <Message info size="small">
          <Message.Header>Processing {fileTypeLabel} File</Message.Header>
          <p>
            {isMP4
              ? 'This MP4 file will be transcribed and an AI description will be generated automatically.'
              : 'This PDF file will be processed and an AI description will be generated automatically.'}
          </p>
        </Message>
      )}

      {processingStatus === ProcessingStatus.PROCESSING && (
        <Message info size="small">
          <Message.Header>Processing in Progress</Message.Header>
          <p>
            {isMP4
              ? 'Transcribing audio and generating AI description...'
              : 'Extracting content and generating AI description...'}
          </p>
        </Message>
      )}
    </StatusContainer>
  );
};
